/**
 * @domain Core/Services
 * 
 * Unified Loading Coordinator - Prevents conflicts between loading services
 * 
 * This service coordinates:
 * - Onboarding flow
 * - Background refresh cycles
 * - Progressive loading phases
 * - Cache warming operations
 * 
 * Prevents the 12-second reload issue during onboarding
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export interface LoadingPhase {
  name: 'initializing' | 'authenticating' | 'onboarding' | 'preloading' | 'ready' | 'complete';
  priority: number;
  allowBackgroundRefresh: boolean;
  allowCacheWarming: boolean;
  allowStateUpdates: boolean;
}

export interface UnifiedLoadingState {
  currentPhase: LoadingPhase;
  isOnboardingActive: boolean;
  isPreloadingActive: boolean;
  backgroundTasksBlocked: boolean;
  lastStateChange: Date;
}

@Injectable({
  providedIn: 'root'
})
export class UnifiedLoadingCoordinatorService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Loading phases in priority order
  private readonly loadingPhases: Record<string, LoadingPhase> = {
    initializing: {
      name: 'initializing',
      priority: 1,
      allowBackgroundRefresh: false,
      allowCacheWarming: false,
      allowStateUpdates: false
    },
    authenticating: {
      name: 'authenticating',
      priority: 2,
      allowBackgroundRefresh: false,
      allowCacheWarming: true,
      allowStateUpdates: false
    },
    onboarding: {
      name: 'onboarding',
      priority: 3,
      allowBackgroundRefresh: false, // 🔥 This prevents the 12-second reload!
      allowCacheWarming: false,
      allowStateUpdates: false
    },
    preloading: {
      name: 'preloading',
      priority: 4,
      allowBackgroundRefresh: false,
      allowCacheWarming: true,
      allowStateUpdates: true
    },
    ready: {
      name: 'ready',
      priority: 5,
      allowBackgroundRefresh: true,
      allowCacheWarming: true,
      allowStateUpdates: true
    },
    complete: {
      name: 'complete',
      priority: 6,
      allowBackgroundRefresh: true,
      allowCacheWarming: true,
      allowStateUpdates: true
    }
  };

  // State management
  private loadingStateSubject = new BehaviorSubject<UnifiedLoadingState>({
    currentPhase: this.loadingPhases['initializing'],
    isOnboardingActive: false,
    isPreloadingActive: false,
    backgroundTasksBlocked: true,
    lastStateChange: new Date()
  });

  public loadingState$ = this.loadingStateSubject.asObservable();
  
  // Derived observables
  public canRefreshBackground$ = this.loadingState$.pipe(
    map(state => state.currentPhase.allowBackgroundRefresh),
    distinctUntilChanged()
  );
  
  public canWarmCache$ = this.loadingState$.pipe(
    map(state => state.currentPhase.allowCacheWarming),
    distinctUntilChanged()
  );
  
  public canUpdateState$ = this.loadingState$.pipe(
    map(state => state.currentPhase.allowStateUpdates),
    distinctUntilChanged()
  );

  constructor() {
    console.log('🎯 UnifiedLoadingCoordinator: Initializing');
    this.initializePhaseTransitions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Set the current loading phase
   */
  public setPhase(phaseName: keyof typeof this.loadingPhases): void {
    const newPhase = this.loadingPhases[phaseName];
    const currentState = this.loadingStateSubject.value;
    
    // Only allow phase transitions to higher priority phases
    if (newPhase.priority >= currentState.currentPhase.priority) {
      console.log(`🎯 Phase transition: ${currentState.currentPhase.name} → ${newPhase.name}`);
      
      const newState: UnifiedLoadingState = {
        ...currentState,
        currentPhase: newPhase,
        backgroundTasksBlocked: !newPhase.allowBackgroundRefresh,
        lastStateChange: new Date()
      };
      
      this.loadingStateSubject.next(newState);
    } else {
      console.warn(`🚫 Phase transition blocked: ${newPhase.name} (priority ${newPhase.priority}) cannot override ${currentState.currentPhase.name} (priority ${currentState.currentPhase.priority})`);
    }
  }

  /**
   * Start onboarding phase - blocks background refresh
   */
  public startOnboarding(): void {
    console.log('🎯 Starting onboarding phase - blocking background tasks');
    this.setPhase('onboarding');
    
    const currentState = this.loadingStateSubject.value;
    this.loadingStateSubject.next({
      ...currentState,
      isOnboardingActive: true
    });
  }

  /**
   * Complete onboarding phase - allows background refresh
   */
  public completeOnboarding(): void {
    this.setPhase('ready');
    
    const currentState = this.loadingStateSubject.value;
    this.loadingStateSubject.next({
      ...currentState,
      isOnboardingActive: false
    });
  }

  /**
   * Check if background refresh is allowed
   */
  public canRefreshBackground(): boolean {
    return this.loadingStateSubject.value.currentPhase.allowBackgroundRefresh;
  }

  /**
   * Check if cache warming is allowed
   */
  public canWarmCache(): boolean {
    return this.loadingStateSubject.value.currentPhase.allowCacheWarming;
  }

  /**
   * Check if state updates are allowed
   */
  public canUpdateState(): boolean {
    return this.loadingStateSubject.value.currentPhase.allowStateUpdates;
  }

  /**
   * Force transition to ready state (for testing)
   */
  public forceReady(): void {
    console.log('🧪 Force transitioning to ready state');
    this.setPhase('ready');
  }

  private initializePhaseTransitions(): void {
    // Auto-transition from initializing to authenticating after 1 second
    setTimeout(() => {
      if (this.loadingStateSubject.value.currentPhase.name === 'initializing') {
        this.setPhase('authenticating');
      }
    }, 1000);
  }

  /**
   * Get current phase info for debugging
   */
  public getCurrentPhaseInfo(): { phase: string; allowRefresh: boolean; allowWarming: boolean } {
    const state = this.loadingStateSubject.value;
    return {
      phase: state.currentPhase.name,
      allowRefresh: state.currentPhase.allowBackgroundRefresh,
      allowWarming: state.currentPhase.allowCacheWarming
    };
  }
}
